/**
 * AdminImmeubleHub — Espace Administratif - Gestion Immobilière (refonte tuiles).
 *
 * Batch #14.96 — Remplace l'ancien empilement Zone1/2/3 par une grille de tuiles
 * cliquables (chacune ouvre une page dédiée).
 *
 * Layout :
 *  1. Header global : titre + 3 boutons (Saisir dépense / Conciliation / Créer bail)
 *  2. Sélecteur Master (combobox searchable)
 *  3. Grille 7 tuiles (visible UNIQUEMENT si master sélectionné)
 *     - Vue portail (bleu)        → /admin/immeubles/:id/portail
 *     - Demandes en attente (mauve) → /admin/immeubles/:id/demandes
 *     - Vérifications & signatures (jaune) → /admin/immeubles/:id/verifications
 *     - Données financières (vert)  → /admin/immeubles/:id/finance
 *     - Comptabilité (orange)        → /admin/immeubles/:id/comptabilite
 *     - Bail en attente (rose)       → /admin/immeubles/:id/baux-en-attente
 *     - Archives (doré)              → /admin/immeubles/:id/archives
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Building2, Eye, Users, ChevronsUpDown, Check, Loader2,
  Inbox, Wallet, Calculator, Clock, FolderTree, FileText, Wrench,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { API, authH } from "@/api";
import { TONE_BG } from "@/constants/tones";


// Les tuiles du hub — chaque tuile = un sous-module
const TILES = [
  {
    id: "portail",
    title: "Vue portail",
    desc: "Aperçu identique au client",
    icon: Eye,
    tone: "blue",
  },
  {
    id: "demandes",
    title: "Demandes en attente",
    desc: "Enquêtes crédit · RL-31 · Renouvellement",
    icon: Inbox,
    tone: "violet",
  },
  {
    id: "suivi-travaux",
    title: "Suivi des travaux",
    desc: "Travaux · Entrepreneur · Factures · Photos",
    icon: Wrench,
    tone: "amber",
  },
  {
    id: "finance",
    title: "Données financières",
    desc: "Revenus · Dépenses · DPA · Résultats",
    icon: Wallet,
    tone: "emerald",
  },
  {
    id: "comptabilite",
    title: "Comptabilité",
    desc: "Conciliation manuelle",
    icon: Calculator,
    tone: "orange",
  },
  // Batch #14.144 — Bail TAL (à côté de Comptabilité) — accès direct au nouveau workflow
  {
    id: "bail-tal",
    title: "Bail TAL",
    desc: "Remplir + signature électronique",
    icon: FileText,
    tone: "indigo",
    href: "/admin/bail-workflow",  // master_id sera ajouté dans openTile
    appendMasterId: true,
  },
  {
    id: "suivi-signatures",
    title: "Suivi signatures",
    desc: "Bail TAL · Renouvellements · Étape en cours",
    icon: Clock,
    tone: "rose",
  },
];


export default function AdminImmeubleHub() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [masters, setMasters] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedId, setSelectedId] = useState(searchParams.get("master_id") || "");
  const [openCombo, setOpenCombo] = useState(false);

  // Charge masters avec expertise immeuble
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/masters?expertise=immeuble`, { headers: authH() });
        setMasters(r.data?.items || []);
        if (!selectedId && (r.data?.items || []).length === 1) {
          setSelectedId(r.data.items[0].id);
        }
      } catch {
        toast.error("Erreur chargement masters");
      } finally {
        setLoadingList(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist master_id dans URL
  useEffect(() => {
    if (selectedId) setSearchParams({ master_id: selectedId }, { replace: true });
  }, [selectedId, setSearchParams]);

  const selectedMaster = useMemo(
    () => masters.find((m) => m.id === selectedId),
    [masters, selectedId]
  );

  const openTile = useCallback((tile) => {
    // Batch #14.144 — Tuile avec href absolu (ex: /admin/bail-workflow) → nav directe
    // Batch #14.213 — Si appendMasterId, on ajoute ?master_id=... pour filtrer la cible
    if (typeof tile === "object" && tile.href) {
      let target = tile.href;
      if (tile.appendMasterId && selectedId) {
        const sep = target.includes("?") ? "&" : "?";
        target = `${target}${sep}master_id=${selectedId}`;
      }
      navigate(target);
      return;
    }
    const tileId = typeof tile === "object" ? tile.id : tile;
    if (!selectedId) {
      toast.message("Sélectionnez un dossier", { description: "Choisissez un client avant d'ouvrir un module." });
      return;
    }
    navigate(`/admin/immeubles/${selectedId}/${tileId}`);
  }, [navigate, selectedId]);

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-immeuble-hub">
      {/* ═══ ZONE HAUTE — 2 colonnes : Tuile Déclaratif (gauche) + Header (droite) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* COL GAUCHE — Tuile « Dossier Immeuble — Déclaratif » */}
        <button
          onClick={() => navigate("/admin/immeuble-dossiers")}
          className="group bg-gradient-to-br from-violet-50 to-violet-100 border-2 border-violet-200 rounded-2xl p-5 sm:p-6 shadow-sm hover:shadow-lg hover:scale-[1.02] active:scale-95 transition-all duration-200 text-left flex flex-col"
          data-testid="immeuble-hub-tile-dossier-declaratif"
        >
          <div className="flex items-start gap-3 mb-2">
            <div className="w-11 h-11 rounded-xl bg-violet-600 text-white flex items-center justify-center shadow group-hover:bg-violet-700 transition">
              <FolderTree className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-violet-900">
                Dossier Immeuble — Déclaratif
              </h2>
              <p className="text-xs text-violet-700 mt-0.5">
                Vue déclarative des clients propriétaires d'immeubles.
              </p>
            </div>
          </div>
          <div className="mt-auto text-[11px] text-violet-700 font-semibold inline-flex items-center gap-1">
            Ouvrir la liste →
          </div>
        </button>

        {/* COL DROITE — Header existant + sélecteur Master */}
        <div className="bg-gradient-to-r from-emerald-50 via-emerald-50 to-white border-2 border-emerald-200 rounded-2xl p-5 sm:p-6 shadow-sm">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow">
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-slate-900">
                  Espace administratif — Gestion immobilière
                </h1>
                <p className="text-xs text-slate-600 mt-0.5">
                  Sélectionnez un dossier client pour accéder aux modules.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sélecteur Master (combobox) */}
        <div className="mt-5">
          <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider block mb-1.5">
            Dossier client
          </label>
          <Popover open={openCombo} onOpenChange={setOpenCombo}>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-expanded={openCombo}
                className="w-full inline-flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm text-left transition shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
                data-testid="immeuble-hub-combobox-trigger"
                disabled={loadingList}
              >
                <span className="truncate flex-1">
                  {loadingList ? (
                    <span className="inline-flex items-center gap-2 text-slate-500">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement…
                    </span>
                  ) : selectedMaster ? (
                    <span className="text-slate-800 font-medium">{selectedMaster.couple_display_name || "(sans nom)"}</span>
                  ) : (
                    <span className="text-slate-400">Choisir un dossier client…</span>
                  )}
                </span>
                <ChevronsUpDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[280px]" align="start">
              <Command>
                <CommandInput placeholder="Rechercher un dossier…" />
                <CommandList>
                  <CommandEmpty>
                    {masters.length === 0
                      ? "Aucun dossier avec expertise Immeuble activée."
                      : "Aucun résultat."}
                  </CommandEmpty>
                  <CommandGroup heading={`${masters.length} dossier(s)`}>
                    {masters.map((m) => (
                      <CommandItem
                        key={m.id}
                        value={`${m.couple_display_name || ""} ${m.primary_name || ""} ${m.secondary_name || ""} ${m.primary_email || ""}`}
                        onSelect={() => { setSelectedId(m.id); setOpenCombo(false); }}
                        className="flex items-start gap-2 py-2 cursor-pointer"
                      >
                        <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${selectedId === m.id ? "opacity-100 text-emerald-600" : "opacity-0"}`} />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-800 truncate">{m.couple_display_name || "(sans nom)"}</div>
                          {(m.primary_name || m.secondary_name) && (
                            <div className="text-[11px] text-slate-500 inline-flex items-center gap-1 mt-0.5">
                              <Users className="w-3 h-3" />
                              {m.primary_name || "?"}{m.secondary_name ? ` + ${m.secondary_name}` : ""}
                            </div>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        </div>{/* fin COL DROITE */}
      </div>{/* fin grid 2 colonnes */}

      {/* ═══ GRILLE TUILES — visible UNIQUEMENT si master sélectionné ═══════ */}
      {!selectedId ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-16 text-center">
          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-slate-700">Sélectionnez un dossier client</h3>
          <p className="text-xs text-slate-500 mt-1">Les modules apparaîtront ci-dessous.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {TILES.map((t) => (
            <button
              key={t.id}
              onClick={() => openTile(t)}
              className={`group bg-gradient-to-br ${TONE_BG[t.tone]} border-2 rounded-2xl p-5 flex flex-col items-center justify-center text-center transition-all duration-200 transform hover:scale-105 active:scale-95 hover:shadow-lg`}
              data-testid={`immeuble-hub-tile-${t.id}`}
            >
              <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center mb-3 group-hover:shadow-md transition">
                <t.icon className="w-7 h-7" />
              </div>
              <span className="font-semibold text-sm mb-0.5">{t.title}</span>
              <span className="text-[10px] opacity-75">{t.desc}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
